import React, { useState } from 'react';
import { Pressable, ScrollView, View, type LayoutChangeEvent } from 'react-native';
import { fmtDate, periodLabel } from '@hrms/shared';
import { addMonths, daysInMonth, pad, todayISO, WEEKDAYS } from '../lib/date';
import { Button, IconButton } from './Button';
import { Field, useInputStyle } from './Form';
import { Icon } from './Icon';
import { Modal } from './Modal';
import { Text } from './Text';
import { radius, useTheme } from './theme';

/** ISO dates for a Monday-first month grid (null = padding cell). */
export function monthCells(month: string): (string | null)[] {
  const [y, m] = month.split('-').map(Number);
  const first = new Date(y, m - 1, 1).getDay(); // 0 = Sun
  const lead = (first + 6) % 7;
  const n = daysInMonth(month);
  const cells: (string | null)[] = Array(lead).fill(null);
  for (let d = 1; d <= n; d++) cells.push(`${month}-${pad(d)}`);
  while (cells.length % 7) cells.push(null);
  return cells;
}

function MonthHeader({ month, onMonthChange, extra, onTitlePress }: { month: string; onMonthChange: (m: string) => void; extra?: React.ReactNode; onTitlePress?: () => void }) {
  const { colors } = useTheme();
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
      <IconButton icon="chevronLeft" label="Previous month" onPress={() => onMonthChange(addMonths(month, -1))} />
      <Pressable disabled={!onTitlePress} onPress={onTitlePress} accessibilityRole={onTitlePress ? 'button' : undefined} style={{ flexDirection: 'row', alignItems: 'center', gap: 4, minWidth: 130, justifyContent: 'center' }}>
        <Text variant="title">{periodLabel(month)}</Text>
        {onTitlePress && <Icon name="chevronDown" size={14} color={colors.muted} />}
      </Pressable>
      <IconButton icon="chevronRight" label="Next month" onPress={() => onMonthChange(addMonths(month, 1))} />
      <View style={{ flex: 1 }} />
      {extra}
    </View>
  );
}

/**
 * Month grid for attendance / leave calendars. Each day cell shows the date and whatever
 * `renderDay(date)` returns; `dayBackground(date)` tints the cell.
 */
export function CalendarMonth({
  month,
  onMonthChange,
  renderDay,
  dayBackground,
  onDayPress,
  headerExtra,
  minCellHeight,
}: {
  month: string;
  onMonthChange: (m: string) => void;
  renderDay?: (date: string) => React.ReactNode;
  dayBackground?: (date: string) => string | undefined;
  onDayPress?: (date: string) => void;
  headerExtra?: React.ReactNode;
  minCellHeight?: number;
}) {
  const { colors } = useTheme();
  const [w, setW] = useState(0);
  const cells = monthCells(month);
  const today = todayISO();
  const cellW = w ? Math.floor(w / 7) : 0;
  const compact = cellW < 90;
  const h = minCellHeight ?? (compact ? 56 : 92);
  return (
    <View>
      <MonthHeader month={month} onMonthChange={onMonthChange} extra={headerExtra} />
      <View onLayout={(e: LayoutChangeEvent) => setW(e.nativeEvent.layout.width)}>
        <View style={{ flexDirection: 'row' }}>
          {WEEKDAYS.map((d) => (
            <View key={d} style={{ width: `${100 / 7}%`, paddingVertical: 6, alignItems: 'center' }}>
              <Text variant="caption">{compact ? d[0] : d}</Text>
            </View>
          ))}
        </View>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', borderTopWidth: 1, borderLeftWidth: 1, borderColor: colors.border }}>
          {cells.map((date, i) => {
            const bg = date ? dayBackground?.(date) : undefined;
            const isToday = date === today;
            return (
              <Pressable
                key={date ?? `pad-${i}`}
                disabled={!date || !onDayPress}
                onPress={() => date && onDayPress?.(date)}
                accessibilityRole={date && onDayPress ? 'button' : undefined}
                accessibilityLabel={date ? fmtDate(date) : undefined}
                style={({ pressed }) => ({
                  width: `${100 / 7}%`,
                  minHeight: h,
                  padding: compact ? 3 : 6,
                  gap: 2,
                  borderRightWidth: 1,
                  borderBottomWidth: 1,
                  borderColor: colors.border,
                  backgroundColor: pressed ? colors.hover : bg ?? (date ? colors.surface : colors.surface2),
                  overflow: 'hidden',
                })}
              >
                {date && (
                  <>
                    <View style={{ alignSelf: compact ? 'center' : 'flex-start', minWidth: 22, height: 22, borderRadius: 11, alignItems: 'center', justifyContent: 'center', backgroundColor: isToday ? colors.primary : 'transparent' }}>
                      <Text size={12} weight={isToday ? 'bold' : 'medium'} color={isToday ? colors.onPrimary : colors.text}>
                        {Number(date.slice(8))}
                      </Text>
                    </View>
                    {renderDay?.(date)}
                  </>
                )}
              </Pressable>
            );
          })}
        </View>
      </View>
    </View>
  );
}

/** Small coloured label inside a calendar cell. */
export function CalChip({ color, children, faded }: { color: string; children: React.ReactNode; faded?: boolean }) {
  return (
    <View style={{ backgroundColor: color + '22', borderLeftWidth: 3, borderLeftColor: color, borderRadius: 4, paddingHorizontal: 4, paddingVertical: 1, opacity: faded ? 0.6 : 1 }}>
      <Text size={10} numberOfLines={1} color={color} weight="semibold">
        {children}
      </Text>
    </View>
  );
}

function DatePicker({ value, min, max, onPick }: { value?: string; min?: string; max?: string; onPick: (d: string) => void }) {
  const { colors } = useTheme();
  const [month, setMonth] = useState((value || todayISO()).slice(0, 7));
  const [years, setYears] = useState(false);
  const today = todayISO();
  const cy = new Date().getFullYear();
  const yFrom = min ? Number(min.slice(0, 4)) : cy - 80;
  const yTo = max ? Number(max.slice(0, 4)) : cy + 5;
  if (years) {
    const list: number[] = [];
    for (let y = yTo; y >= yFrom; y--) list.push(y);
    return (
      <ScrollView style={{ maxHeight: 320 }} contentContainerStyle={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
        {list.map((y) => (
          <Button key={y} size="sm" title={String(y)} variant={String(y) === month.slice(0, 4) ? 'primary' : 'ghost'} onPress={() => { setMonth(`${y}${month.slice(4)}`); setYears(false); }} />
        ))}
      </ScrollView>
    );
  }
  const cells = monthCells(month);
  return (
    <View>
      <MonthHeader month={month} onMonthChange={setMonth} onTitlePress={() => setYears(true)} />
      <View style={{ flexDirection: 'row' }}>
        {WEEKDAYS.map((d) => (
          <View key={d} style={{ width: `${100 / 7}%`, alignItems: 'center', paddingVertical: 4 }}>
            <Text variant="caption">{d.slice(0, 2)}</Text>
          </View>
        ))}
      </View>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
        {cells.map((d, i) => {
          const disabled = !d || (!!min && d < min) || (!!max && d > max);
          const sel = d === value;
          return (
            <View key={d ?? `p${i}`} style={{ width: `${100 / 7}%`, aspectRatio: 1, padding: 2 }}>
              {d && (
                <Pressable
                  disabled={disabled}
                  onPress={() => onPick(d)}
                  accessibilityRole="button"
                  accessibilityLabel={fmtDate(d)}
                  accessibilityState={{ selected: sel, disabled }}
                  style={({ pressed }) => ({ flex: 1, borderRadius: radius.md, alignItems: 'center', justifyContent: 'center', backgroundColor: sel ? colors.primary : pressed ? colors.hover : 'transparent', borderWidth: d === today && !sel ? 1 : 0, borderColor: colors.primary, opacity: disabled ? 0.3 : 1 })}
                >
                  <Text color={sel ? colors.onPrimary : colors.text} weight={sel ? 'bold' : 'regular'}>
                    {Number(d.slice(8))}
                  </Text>
                </Pressable>
              )}
            </View>
          );
        })}
      </View>
    </View>
  );
}

/** Date input (YYYY-MM-DD) with a calendar modal — no native date-picker dependency. */
export function DateField({
  label,
  value,
  onChange,
  min,
  max,
  required,
  error,
  hint,
  disabled,
  placeholder = 'Select date',
  clearable,
  testID,
}: {
  label?: string;
  value: string | null | undefined;
  onChange: (iso: string) => void;
  min?: string;
  max?: string;
  required?: boolean;
  error?: string;
  hint?: string;
  disabled?: boolean;
  placeholder?: string;
  clearable?: boolean;
  testID?: string;
}) {
  const { colors } = useTheme();
  const [open, setOpen] = useState(false);
  const base = useInputStyle(!!error, disabled);
  return (
    <Field label={label} required={required} error={error} hint={hint}>
      <Pressable testID={testID} accessibilityRole="button" accessibilityLabel={`${label ?? 'Date'}: ${value ? fmtDate(value) : placeholder}`} disabled={disabled} onPress={() => setOpen(true)} style={[base, { flexDirection: 'row', alignItems: 'center', gap: 8 }]}>
        <Icon name="calendar" size={16} color={colors.muted} />
        <Text style={{ flex: 1 }} color={value ? colors.text : colors.faint}>
          {value ? fmtDate(value) : placeholder}
        </Text>
      </Pressable>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={label ?? 'Select date'}
        width={380}
        footer={
          <>
            {clearable && <Button title="Clear" variant="ghost" onPress={() => { onChange(''); setOpen(false); }} />}
            {(!min || todayISO() >= min) && (!max || todayISO() <= max) && <Button title="Today" onPress={() => { onChange(todayISO()); setOpen(false); }} />}
          </>
        }
      >
        <DatePicker value={value || undefined} min={min} max={max} onPick={(d) => { onChange(d); setOpen(false); }} />
      </Modal>
    </Field>
  );
}
